/**
 * Die Daueraufnahme kommt von selbst zurueck. (06.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Außerdem hat die Aufnahme wieder auf Pause geschalten. Ich glaub,
 * ich hab etwas gefragt. Danach sollte es automatisch weiterlaufen, weil ich
 * unter Einstellungen eingestellt habe, Daueraufnahme. Weil es geht ja bei
 * der automatischen Aufnahme darum, dass maximal viele Informationen
 * gesammelt werden."*
 *
 * ## Was vorher fehlte
 *
 * Es gab schon eine Pause-Wache (`native-recorder.ts`, 23.07.). Sie hat zwei
 * Luecken, und beide trafen genau diesen Fall:
 *
 *   1. **Sie gibt auf.** Steht die native Tages-Absicht (`dayIntent`) beim
 *      Wecken nicht, kehrt sie ohne neuen Termin zurueck. Danach weckt sie
 *      nur noch ein Zustandswechsel — und der kommt nicht mehr, weil der
 *      Zustand ja schon „pausiert" ist. Eine einzige Flanke genuegt, und die
 *      Aufnahme steht bis zum naechsten Handgriff.
 *   2. **Sie kennt nur „pausiert".** Ist die Sitzung inzwischen beendet —
 *      der Server raeumt eine stumme Sitzung nach 25 Minuten selbst ab —,
 *      dann steht der Rekorder auf „idle", und es gibt nichts mehr
 *      fortzusetzen. Niemand faengt neu an.
 *
 * ## Die Regel hier
 *
 * Der Schalter in den Einstellungen ist eine **Absicht**, kein einmaliger
 * Befehl. Solange er an ist, sorgt diese Wache dafuer, dass die Aufnahme
 * laeuft: sie setzt Pausen fort, die kein aktiver Grund mehr haelt, und sie
 * beginnt eine neue Sitzung, wenn gar keine mehr da ist.
 *
 * ⚠️ **Sie funkt nie dazwischen.** Laeuft gerade ein Grund (Push-to-Talk,
 *    Diktat, Wiedergabe, Anruf), wartet sie und sieht spaeter wieder nach.
 *    Und sie prueft immer den ECHTEN Zustand aus der nativen Engine, nie den
 *    gemerkten — derselbe Fehler wurde in diesem Haus schon dreimal gemacht.
 *
 * ⚠️ **Ein bewusstes Aus bleibt aus.** Wer den Schalter ausmacht oder die
 *    Aufnahme selbst stoppt, wird nicht ueberstimmt: ohne `alwaysRecord`
 *    tut diese Wache nichts.
 */

import {
  bewusstBeendet,
  getRecorderState,
  hatOffenePausenGruende,
  isRecorderAvailable,
  setAlwaysDayRecording,
  startRecording,
  syncFromNative,
  wacheMussWarten,
  wacheSetztFort,
} from "./native-recorder.js";
import {
  darfWecken,
  istNutzerPause,
  leseZuletztLief,
  merkeLaeuft,
  merkeNeubeginn,
  setzeWeckAbstand,
  urteilFuerWache,
} from "./pause-besitzer.js";
import { meldeMikroAnlass } from "./mikro-spur.js";
import { hapticTap, playBeep } from "./audio-feedback.js";

/** Wie oft nachgesehen wird, solange alles in Ordnung ist. */
export const WACHE_TAKT_MS = 15_000;
/** Nach so vielen Fehlschlaegen in Folge gibt es einen Alarm. */
export const FEHLSCHLAEGE_BIS_ALARM = 2;
/** Nach dem Alarm sieht die Wache nur noch so selten nach (kein Journal-Sturm). */
export const WACHE_NACH_ALARM_MS = 60_000;
/**
 * Kuerzerer Anlauf, wenn gerade ein Grund gilt: Das Diktat dauert Sekunden,
 * nicht Minuten — so kommt die Aufnahme schnell zurueck, ohne zu draengeln.
 */
export const WACHE_KURZ_MS = 10_000;

let timer: number | null = null;
let laeuftGerade = false;
let fehlschlaege = 0;
/** Der Alarm geht einmal je Serie von Fehlschlaegen, bis zum naechsten Erfolg. */
let alarmGesendet = false;

/** Nur fuer Tests. */
export function _wacheVergessen(): void {
  fehlschlaege = 0;
  alarmGesendet = false;
}

/** Was die Wache beim Nachsehen vorgefunden und getan hat. */
type WachBefund =
  | "aus" /* Schalter steht auf aus — nichts zu tun */
  | "laeuft" /* alles in Ordnung */
  | "wartet" /* ein Grund haelt die Pause gerade zu Recht */
  | "fortgesetzt" /* eine Pause ohne Grund wurde fortgesetzt */
  | "fehlgeschlagen" /* Fortsetzen kam nicht an */
  | "nutzer-pause" /* Martin hat pausiert — bleibt so */
  | "unbekannt" /* Pause ohne belegte Ursache — nur Anzeige, kein Eingriff */
  | "neu-begonnen" /* es gab keine Sitzung mehr, also eine neue */
  | "kein-rekorder"; /* im Browser ohne native Huelle */

export interface WacheUmgebung {
  /** Steht der Schalter „durchgehend aufnehmen" auf an? */
  dauerGewuenscht: () => boolean;
  /** Hat der Nutzer die Aufnahme selbst beendet? Dann bleibt sie aus. */
  bewusstAus: () => boolean;
  zustand: () => string;
  offeneGruende: () => boolean;
  abgleichen: () => Promise<void>;
  /** Direkt fortsetzen. Wirkt nie auf eine Pause des Nutzers. */
  fortsetzen: () => Promise<unknown>;
  /** Gehoert die Pause dem Nutzer? Dann tut die Wache nichts. */
  nutzerPause?: () => boolean;
  /** Urteil ueber die Pause: nutzer / belegt / unbekannt. */
  urteil?: () => "nutzer" | "belegt" | "unbekannt";
  /** Haelt Anruf oder Vespera-Stimme das Mikro? Dann warten, ohne Fehlschlag. */
  mussWarten?: () => Promise<boolean>;
  /** Neue Sitzung ohne Zutun des Nutzers begonnen. */
  neubeginnMerken?: (vorher: number) => void;
  /** Wann es zuletzt lief (ms), 0 = unbekannt. */
  zuletztLief?: () => number;
  /** Zweimal in Folge nicht fortgesetzt. */
  alarm?: (text: string) => void;
  /** Ton und Vibration (selbst auf 60 s begrenzt). */
  weckruf?: () => void;
  beginnen: () => Promise<void>;
  absichtSetzen: (an: boolean) => Promise<void>;
  verfuegbar: () => boolean;
  melde?: (text: string) => void;
}

/**
 * Einmal nachsehen und, wenn noetig, handeln.
 *
 * Bewusst als reine Funktion mit einer Umgebung: So laesst sich jeder Fall
 * pruefen, ohne native Huelle und ohne Zeit vergehen zu lassen.
 */
export async function seheNach(u: WacheUmgebung): Promise<WachBefund> {
  if (!u.dauerGewuenscht()) return "aus";
  /*
   * ⚠️ Ein Stopp von Hand laesst den Schalter in den Einstellungen
   *    absichtlich stehen (native-recorder.ts). Ohne diese Zeile wuerfe die
   *    Wache die Aufnahme binnen einer Minute wieder an und machte aus einer
   *    Entscheidung eine Empfehlung.
   */
  if (u.bewusstAus()) return "aus";
  if (!u.verfuegbar()) return "kein-rekorder";

  // Immer der ECHTE Zustand. Der gemerkte ist bis zu 15 s alt, und in genau
  // dieser Luecke sind hier schon dreimal Entscheidungen falsch gefallen.
  await u.abgleichen();
  const zustand = u.zustand();

  if (zustand === "recording") {
    fehlschlaege = 0;
    alarmGesendet = false;
    setzeWeckAbstand(false);
    merkeLaeuft();
    return "laeuft";
  }

  // Ein laufender Grund hat Vorrang. Push-to-Talk, Diktat und Wiedergabe
  // brauchen das Mikro; wer ihnen dazwischenfunkt, nimmt Stille auf.
  if (u.offeneGruende()) return "wartet";

  if (zustand === "paused") {
    /*
     * ⚠️ Eine Pause von Martin bleibt, solange er sie nicht selbst aufhebt —
     *    auch nach dem Neustart der Seite (der Besitzer liegt im Speicher).
     */
    if (u.nutzerPause?.() || u.urteil?.() === "nutzer") {
      fehlschlaege = 0;
      return "nutzer-pause";
    }
    /*
     * ⚠️ Ohne BELEGTE Ursache (Wiedergabe, Unterbrechung, Mikro verweigert,
     *    Medien-Neustart, Haeppchen-Abbruch, eigener Grund) fassen wir nichts
     *    an: Wir koennten Martins Pause aufheben, die wir nur nicht erkennen
     *    (die Engine nennt den Grund nach einem Seiten-Neustart nicht mehr).
     */
    if (u.urteil?.() === "unbekannt") return "unbekannt";
    // Anruf / Vespera spricht: die Engine lehnt das Fortsetzen ab. Kein
    // Fehlschlag, kein Ton, kein Alarm.
    if (await u.mussWarten?.()) return "wartet";
    await u.fortsetzen();
    await u.abgleichen();
    if (u.zustand() === "recording") {
      fehlschlaege = 0;
      alarmGesendet = false;
      setzeWeckAbstand(false);
      u.melde?.("[aufnahme-wache] Pause ohne Grund — fortgesetzt");
      return "fortgesetzt";
    }
    fehlschlaege++;
    u.weckruf?.();
    u.melde?.("[aufnahme-wache] Fortsetzen kam nicht an");
    if (fehlschlaege >= FEHLSCHLAEGE_BIS_ALARM && !alarmGesendet) {
      alarmGesendet = true;
      setzeWeckAbstand(true);
      u.alarm?.("Aufnahme steht, Fortsetzen scheitert");
    }
    return "fehlgeschlagen";
  }

  if (zustand === "idle") {
    // Die Sitzung ist weg (vom Server abgeraeumt, von iOS beendet, nach
    // einem Neustart). Fortsetzen geht nicht mehr — also neu beginnen und
    // die Absicht gleich mitgeben, damit die native Seite sie kennt.
    const vorher = u.zuletztLief?.() ?? 0;
    await u.beginnen();
    await u.absichtSetzen(true);
    u.neubeginnMerken?.(vorher);
    u.melde?.("[aufnahme-wache] keine Sitzung mehr — neu begonnen");
    return "neu-begonnen";
  }

  // „finishing" und alles Unbekannte: nicht anfassen, gleich nochmal sehen.
  return "wartet";
}

/** Wie lange bis zum naechsten Blick — kurz, solange etwas offen ist. */
/**
 * Wie lange bis zum naechsten Blick.
 *
 * ⚠️⚠️ 17.09.2026 — NACH EINEM EINGRIFF WIRD KURZ NACHGESEHEN.
 *
 * Martin, waehrend einer Kursaufnahme: *„So schwer stelle ich mir das
 * wirklich nicht vor, dass einfach die Aufnahme sauber stabil durchlaeuft."*
 *
 * Bisher gab es nur zwei Takte: 15 s, solange ein Grund die Pause haelt,
 * sonst 60 s. „Fortgesetzt" und „neu begonnen" fielen damit in die lange
 * Frist — ausgerechnet die beiden Faelle, in denen gerade etwas repariert
 * wurde und noch niemand weiss, ob es hielt. Ging der Neubeginn daneben
 * (das Mikro war noch belegt), stand die Aufnahme eine volle Minute, bevor
 * ueberhaupt jemand hinsah.
 *
 * Deshalb: nach jedem EINGRIFF derselbe kurze Takt wie beim Warten. Wer
 * eben gehandelt hat, prueft bald nach; wer nur bestaetigt hat, dass alles
 * laeuft, darf sich Zeit lassen.
 */
export function naechsterBlick(befund: WachBefund): number {
  const eingegriffen =
    befund === "wartet" ||
    befund === "fortgesetzt" ||
    befund === "neu-begonnen";
  if (befund === "fehlgeschlagen") {
    return fehlschlaege >= FEHLSCHLAEGE_BIS_ALARM ? WACHE_NACH_ALARM_MS : WACHE_KURZ_MS;
  }
  if (befund === "unbekannt") return WACHE_TAKT_MS;
  return eingegriffen ? WACHE_KURZ_MS : WACHE_TAKT_MS;
}

/**
 * Ton und Vibration bei unfreiwilliger Pause im Vordergrund — hoechstens
 * alle 60 s, damit es nie nervt. Im Hintergrund bleibt es still.
 */
export function weckeBeiUnfreiwilligerPause(): boolean {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return false;
  if (!darfWecken()) return false;
  try {
    hapticTap(35);
    playBeep("achtung");
  } catch {
    /* best effort */
  }
  return true;
}

export function umgebungAusApp(): WacheUmgebung {
  return {
    dauerGewuenscht: () => {
      try {
        // Direkt aus dem Speicher: settings.ts liest denselben Schluessel,
        // ein Import waere hier nur ein Umweg mit Ladezeit.
        return localStorage.getItem("voice-v8.alwaysRecord") === "1";
      } catch {
        return false;
      }
    },
    bewusstAus: () => bewusstBeendet(),
    zustand: () => getRecorderState(),
    offeneGruende: () => hatOffenePausenGruende(),
    abgleichen: () => syncFromNative().catch(() => undefined),
    fortsetzen: () => wacheSetztFort(),
    nutzerPause: () => istNutzerPause(),
    urteil: () => urteilFuerWache(),
    mussWarten: () => wacheMussWarten(),
    zuletztLief: () => leseZuletztLief(),
    neubeginnMerken: (vorher) => {
      merkeNeubeginn(vorher);
    },
    alarm: (t) => {
      meldeMikroAnlass("aufnahme-wache-alarm", t);
    },
    weckruf: () => {
      weckeBeiUnfreiwilligerPause();
    },
    beginnen: async () => {
      await startRecording();
    },
    absichtSetzen: async (an) => {
      await setAlwaysDayRecording(an);
    },
    verfuegbar: () => isRecorderAvailable(),
    melde: (t) => console.warn(t),
  };
}

/** Beim App-Start aufrufen. Idempotent: ein zweiter Aufruf tut nichts. */
export function starteAufnahmeWache(umgebung?: WacheUmgebung): void {
  if (timer !== null) return;
  const u = umgebung ?? umgebungAusApp();
  /*
   * 06.09.2026 — auch beim Zurueckkommen sofort nachsehen.
   *
   * Martin: *„Es ist immer noch pausiert, obwohl es eigentlich an sein
   * sollte. Ich habe nie auf Pause gedrueckt."* Der Takt laeuft nur, solange
   * die Seite lebt; liegt das Telefon eine Stunde in der Tasche, sieht
   * niemand nach. Wer die App oeffnet, soll nicht noch eine Minute warten.
   */
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible" || laeuftGerade) return;
      laeuftGerade = true;
      void seheNach(u)
        .catch(() => undefined)
        .then(() => {
          laeuftGerade = false;
        });
    });
  }
  const plane = (ms: number): void => {
    timer = window.setTimeout(() => {
      timer = null;
      if (laeuftGerade) {
        plane(WACHE_KURZ_MS);
        return;
      }
      laeuftGerade = true;
      void (async () => {
        let befund: WachBefund = "wartet";
        try {
          befund = await seheNach(u);
        } catch {
          /* Ein Fehler darf die Wache nie beenden — sie ist das Netz. */
        }
        laeuftGerade = false;
        plane(naechsterBlick(befund));
      })();
    }, ms);
  };
  plane(WACHE_TAKT_MS);
}
